import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'

import { AppShell } from '@/components/layout/app-shell'
import { PublicShell } from '@/components/layout/public-shell'
import { ensureSession, hasPermission, hasRole } from '@/features/auth/api'
import { ApplicationsPage } from '@/routes/applications'
import { CheckoutPage } from '@/routes/checkout'
import { CompanyPage } from '@/routes/company'
import { CompanyPublicPage } from '@/routes/company-public'
import { ContestDetailPage } from '@/routes/contest-detail'
import { ContestsPage } from '@/routes/contests'
import { ContractsPage } from '@/routes/contracts'
import { CourseDetailPage } from '@/routes/course-detail'
import { CoursesPage } from '@/routes/courses'
import { HomePage } from '@/routes/home'
import { JobBoardPage } from '@/routes/jobs'
import { JobDetailPage } from '@/routes/job-detail'
import { LearnPage } from '@/routes/learn'
import { ForgotPasswordPage } from '@/routes/forgot-password'
import { LoginPage } from '@/routes/login'
import { ResetPasswordPage } from '@/routes/reset-password'
import { VerifyEmailPage } from '@/routes/verify-email'
import { ManageCoursesPage } from '@/routes/manage-courses'
import { MyContestsPage } from '@/routes/my-contests'
import { MyServicesPage } from '@/routes/my-services'
import { MediationPage } from '@/routes/mediation'
import { AdminUsersPage } from '@/routes/admin-users'
import { PanduanPage } from '@/routes/panduan'
import { PromosiSayaPage } from '@/routes/promosi-saya'
import { LiniMasaPage } from '@/routes/lini-masa'
import { CariPage } from '@/routes/cari'
import { OrangPage } from '@/routes/orang'
import { GrupPage } from '@/routes/grup'
import { GrupDetailPage } from '@/routes/grup-detail'
import { PromoManagerPage } from '@/routes/promo-manager'
import { MyCoursesPage } from '@/routes/my-courses'
import { MyProjectsPage } from '@/routes/my-projects'
import { NewProjectPage } from '@/routes/job-new'
import { NotFoundPage } from '@/routes/not-found'
import { NotificationsPage } from '@/routes/notifications'
import { OrdersPage } from '@/routes/orders'
import { ProfilePage } from '@/routes/profile'
import { ProposalsPage } from '@/routes/proposals'
import { RegisterPage } from '@/routes/register'
import { ServiceDetailPage } from '@/routes/service-detail'
import { ServiceEditorPage } from '@/routes/service-editor'
import { ServiceOrdersPage } from '@/routes/service-orders'
import { ServicesPage } from '@/routes/services'
import { TalentDetailPage } from '@/routes/talent-detail'
import { TalentPage } from '@/routes/talent'
import { VacanciesPage } from '@/routes/vacancies'
import { VacancyDetailPage } from '@/routes/vacancy-detail'
import { VacancyPipelinePage } from '@/routes/vacancy-pipeline'

/**
 * Halaman berat dimuat terpisah.
 *
 * Dasbor membawa pustaka grafik, sedangkan editor kelas, rincian kontrak,
 * dompet, dan panel internal hanya dibuka sebagian pengguna. Memuatnya saat
 * dibutuhkan menjaga unduhan pertama tetap ringan bagi pengunjung yang cuma
 * membuka katalog.
 */
const DashboardPage = lazyRouteComponent(() => import('@/routes/dashboard'), 'DashboardPage')
const CourseEditorPage = lazyRouteComponent(() => import('@/routes/course-editor'), 'CourseEditorPage')
const ContractDetailPage = lazyRouteComponent(() => import('@/routes/contract-detail'), 'ContractDetailPage')
const MediationDetailPage = lazyRouteComponent(() => import('@/routes/mediation-detail'), 'MediationDetailPage')
const WalletPage = lazyRouteComponent(() => import('@/routes/wallet'), 'WalletPage')
const FinancePage = lazyRouteComponent(() => import('@/routes/finance'), 'FinancePage')

/** Context router memuat cache kueri agar penjaga rute dapat memakainya. */
interface RouterContext {
  queryClient: QueryClient
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
})

// ---------------------------------------------------------------------------
// Pembantu penyaring parameter query
// ---------------------------------------------------------------------------

/** Bentuk parameter query katalog kelas. */
interface CatalogSearch {
  q?: string
  category?: string
  level?: string
  format?: string
  sort?: string
  page?: number
}

/** Bentuk parameter query papan proyek dan direktori talenta. */
interface DirectorySearch {
  q?: string
  skill?: string
  page?: number
}

/** Bentuk parameter query papan lowongan kerja. */
interface VacancySearch {
  q?: string
  category?: string
  employment_type?: string
  work_arrangement?: string
  experience_level?: string
  city?: string
  page?: number
}

/** Bentuk parameter query daftar sayembara. */
interface ContestSearch {
  status?: string
  page?: number
}

/** Bentuk parameter query halaman masuk. */
interface LoginSearch {
  redirect?: string
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

function readPage(value: unknown): number | undefined {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 1 ? parsed : undefined
}

// ---------------------------------------------------------------------------
// Halaman publik
// ---------------------------------------------------------------------------

const publicLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public',
  component: () => (
    <PublicShell>
      <Outlet />
    </PublicShell>
  ),
})

const homeRoute = createRoute({ getParentRoute: () => publicLayout, path: '/', component: HomePage })

const coursesRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/kelas',
  component: CoursesPage,
  validateSearch: (search: Record<string, unknown>): CatalogSearch => ({
    q: readString(search.q),
    category: readString(search.category),
    level: readString(search.level),
    format: readString(search.format),
    sort: readString(search.sort),
    page: readPage(search.page),
  }),
})

const courseDetailRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/kelas/$slug',
  component: CourseDetailPage,
})

const jobsRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/proyek',
  component: JobBoardPage,
  validateSearch: (search: Record<string, unknown>): DirectorySearch => ({
    q: readString(search.q),
    skill: readString(search.skill),
    page: readPage(search.page),
  }),
})

const jobDetailRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/proyek/$jobId',
  component: JobDetailPage,
})

const talentRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/talenta',
  component: TalentPage,
  validateSearch: (search: Record<string, unknown>): DirectorySearch => ({
    q: readString(search.q),
    skill: readString(search.skill),
    page: readPage(search.page),
  }),
})

const talentDetailRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/talenta/$expertId',
  component: TalentDetailPage,
})

const vacanciesRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/lowongan',
  component: VacanciesPage,
  validateSearch: (search: Record<string, unknown>): VacancySearch => ({
    q: readString(search.q),
    category: readString(search.category),
    employment_type: readString(search.employment_type),
    work_arrangement: readString(search.work_arrangement),
    experience_level: readString(search.experience_level),
    city: readString(search.city),
    page: readPage(search.page),
  }),
})

const vacancyDetailRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/lowongan/$vacancyId',
  component: VacancyDetailPage,
})

const companyPublicRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/perusahaan/$slug',
  component: CompanyPublicPage,
})

const servicesRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/jasa',
  component: ServicesPage,
  validateSearch: (search: Record<string, unknown>): CatalogSearch => ({
    q: readString(search.q),
    category: readString(search.category),
    sort: readString(search.sort),
    page: readPage(search.page),
  }),
})

const serviceDetailRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/jasa/$slug',
  component: ServiceDetailPage,
})

const contestsRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/sayembara',
  component: ContestsPage,
  validateSearch: (search: Record<string, unknown>): ContestSearch => ({
    status: readString(search.status),
    page: readPage(search.page),
  }),
})

const contestDetailRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/sayembara/$contestId',
  component: ContestDetailPage,
})

// ---------------------------------------------------------------------------
// Autentikasi
// ---------------------------------------------------------------------------

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/masuk',
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    // Tujuan setelah masuk dibatasi pada jalur internal; tanpa pembatasan
    // ini, tautan masuk dapat dipakai mengarahkan pengguna ke situs luar
    // setelah berhasil masuk.
    redirect: readInternalPath(search.redirect),
  }),
})

function readInternalPath(value: unknown): string | undefined {
  const path = readString(value)
  if (!path || !path.startsWith('/') || path.startsWith('//')) return undefined
  return path
}

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/daftar',
  component: RegisterPage,
})

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lupa-sandi',
  component: ForgotPasswordPage,
})

// Token dibawa lewat parameter kueri karena tautannya datang dari surel,
// dan tautan surel tidak dapat mengirim badan permintaan.
interface TokenSearch {
  token?: string
}

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/atur-ulang-sandi',
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>): TokenSearch => ({
    token: readString(search.token),
  }),
})

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verifikasi-email',
  component: VerifyEmailPage,
  validateSearch: (search: Record<string, unknown>): TokenSearch => ({
    token: readString(search.token),
  }),
})

// ---------------------------------------------------------------------------
// Area yang membutuhkan sesi
// ---------------------------------------------------------------------------

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: async ({ context, location }) => {
    const session = await ensureSession(context.queryClient)
    if (!session) {
      // Halaman tujuan dibawa serta supaya pengguna kembali ke tempatnya
      // setelah berhasil masuk.
      throw redirect({ to: '/masuk', search: { redirect: location.pathname } })
    }
    return { session }
  },
})

const checkoutRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/checkout/$orderId',
  component: () => (
    <PublicShell>
      <CheckoutPage />
    </PublicShell>
  ),
})

const appLayout = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/app',
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
})

// Beranda aplikasi adalah lini masa, seperti Threads. Ringkasan angka —
// kelas berjalan, saldo, pesanan — pindah ke menu.
const berandaRoute = createRoute({ getParentRoute: () => appLayout, path: '/', component: LiniMasaPage })
const dashboardRoute = createRoute({ getParentRoute: () => appLayout, path: 'ringkasan', component: DashboardPage })
const cariRoute = createRoute({ getParentRoute: () => appLayout, path: 'cari', component: CariPage })
const orangRoute = createRoute({ getParentRoute: () => appLayout, path: 'orang/$userId', component: OrangPage })
const myCoursesRoute = createRoute({ getParentRoute: () => appLayout, path: 'kelas-saya', component: MyCoursesPage })
const learnRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'belajar/$enrollmentId',
  component: LearnPage,
})
const ordersRoute = createRoute({ getParentRoute: () => appLayout, path: 'pesanan', component: OrdersPage })
const notificationsRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'notifikasi',
  component: NotificationsPage,
})
const profileRoute = createRoute({ getParentRoute: () => appLayout, path: 'profil', component: ProfilePage })

const manageCoursesRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'kelola-kelas',
  component: ManageCoursesPage,
})
const courseEditorRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'kelola-kelas/$courseId',
  component: CourseEditorPage,
})
const walletRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'dompet',
  component: WalletPage,
})

const myProjectsRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'proyek',
  component: MyProjectsPage,
})
const newProjectRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'proyek/baru',
  component: NewProjectPage,
})
const proposalsRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'proposal',
  component: ProposalsPage,
})
const contractsRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'kontrak',
  component: ContractsPage,
})
const contractDetailRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'kontrak/$contractId',
  component: ContractDetailPage,
})

const applicationsRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'lamaran',
  component: ApplicationsPage,
})

const companyRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'perusahaan',
  component: CompanyPage,
})

const vacancyPipelineRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'perusahaan/lowongan/$vacancyId',
  component: VacancyPipelinePage,
})

const myServicesRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'jasa',
  component: MyServicesPage,
})

const serviceEditorRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'jasa/$listingId',
  component: ServiceEditorPage,
})

const serviceOrdersRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'pesanan-jasa',
  component: ServiceOrdersPage,
})

const myContestsRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'sayembara',
  component: MyContestsPage,
})

/**
 * Rute internal dijaga dua lapis: penjaga di sini hanya menghindarkan
 * pengguna dari halaman kosong, sedangkan penegakan sesungguhnya ada di
 * server. Menyembunyikan menu bukan pengendalian akses (SDLC §8.8).
 */
const financeRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'keuangan',
  component: FinancePage,
  beforeLoad: async ({ context }) => {
    const session = await ensureSession(context.queryClient)
    if (!hasPermission(session, 'payout.approve')) throw redirect({ to: '/app' })
  },
})

const mediationRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'mediasi',
  component: MediationPage,
  beforeLoad: async ({ context }) => {
    const session = await ensureSession(context.queryClient)
    if (!hasRole(session, 'mediator', 'super_admin')) throw redirect({ to: '/app' })
  },
})

const mediationDetailRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'mediasi/$disputeId',
  component: MediationDetailPage,
  beforeLoad: async ({ context }) => {
    const session = await ensureSession(context.queryClient)
    if (!hasRole(session, 'mediator', 'super_admin')) throw redirect({ to: '/app' })
  },
})

const promoRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'promosi',
  component: PromoManagerPage,
  beforeLoad: async ({ context }) => {
    const session = await ensureSession(context.queryClient)
    if (!hasPermission(session, 'promo.manage')) throw redirect({ to: '/app' })
  },
})

const adminUsersRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'pengguna',
  component: AdminUsersPage,
  beforeLoad: async ({ context }) => {
    const session = await ensureSession(context.queryClient)
    if (!hasPermission(session, 'user.manage')) throw redirect({ to: '/app' })
  },
})

const panduanRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: 'panduan',
  component: PanduanPage,
})

const promosiSayaRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'promosi-saya',
  component: PromosiSayaPage,
})

// Alamat lama lini masa tetap hidup untuk tautan yang sudah terlanjur
// dibagikan atau tersimpan.
const liniMasaRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'lini-masa',
  beforeLoad: () => {
    throw redirect({ to: '/app' })
  },
})

const grupRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'grup',
  component: GrupPage,
})

const grupDetailRoute = createRoute({
  getParentRoute: () => appLayout,
  path: 'grup/$slug',
  component: GrupDetailPage,
})

const routeTree = rootRoute.addChildren([
  publicLayout.addChildren([
    homeRoute,
    coursesRoute,
    courseDetailRoute,
    jobsRoute,
    jobDetailRoute,
    talentRoute,
    talentDetailRoute,
    vacanciesRoute,
    vacancyDetailRoute,
    companyPublicRoute,
    servicesRoute,
    serviceDetailRoute,
    contestsRoute,
    contestDetailRoute,
    panduanRoute,
  ]),
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  verifyEmailRoute,
  registerRoute,
  authenticatedRoute.addChildren([
    checkoutRoute,
    appLayout.addChildren([
      berandaRoute,
      dashboardRoute,
      cariRoute,
      orangRoute,
      myCoursesRoute,
      learnRoute,
      ordersRoute,
      notificationsRoute,
      profileRoute,
      manageCoursesRoute,
      courseEditorRoute,
      walletRoute,
      myProjectsRoute,
      newProjectRoute,
      proposalsRoute,
      contractsRoute,
      contractDetailRoute,
      applicationsRoute,
      companyRoute,
      vacancyPipelineRoute,
      myServicesRoute,
      serviceEditorRoute,
      serviceOrdersRoute,
      myContestsRoute,
      financeRoute,
      mediationRoute,
      promoRoute,
      adminUsersRoute,
      promosiSayaRoute,
      liniMasaRoute,
      grupRoute,
      grupDetailRoute,
      mediationDetailRoute,
    ]),
  ]),
])

/** Layar tunggu antarhalaman. */
function RouteLoading() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center">
      <span className="size-10 animate-spin rounded-full border-2 border-line border-t-brand" />
      <p className="mono-label text-[11px] text-subtle">Memuat halaman</p>
    </div>
  )
}

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    // Pramuat saat kursor menyentuh tautan membuat perpindahan halaman
    // terasa seketika tanpa mengunduh seluruh aplikasi di muka.
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 10_000,
    scrollRestoration: true,
    defaultNotFoundComponent: NotFoundPage,

    // Selama halaman tujuan belum siap, router menahan halaman sebelumnya
    // di layar. Pada perpindahan biasa itu terasa mulus, tetapi sesudah
    // masuk hasilnya membingungkan: alamat sudah berubah ke /app sementara
    // yang terlihat masih formulir masuk, sehingga pengguna mengira
    // tombolnya tidak bekerja lalu menekannya lagi.
    //
    // Ambang 150 ms membuat perpindahan yang memang cepat tidak berkedip,
    // dan durasi minimum menahan layar tunggu cukup lama untuk terbaca
    // alih-alih berkelebat.
    defaultPendingMs: 150,
    defaultPendingMinMs: 400,
    defaultPendingComponent: RouteLoading,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
